import { join } from "node:path";
import { Agent, type AgentMessage, type StreamFn, type ThinkingLevel } from "@apholdings/jensen-agent-core";
import {
	assertValidChatCompletionsPayload,
	assertValidResponsesPayload,
	type Message,
	type Model,
} from "@apholdings/jensen-ai";
import { getAgentDir, getDocsPath } from "../config.js";
import { AgentSession } from "./agent-session.js";
import { AuthStorage } from "./auth-storage.js";
import {
	type ContextCapabilityOverrides,
	ContextGovernor,
	EvidenceFileStore,
	resolveContextCapability,
	type ToolVirtualizationRecord,
} from "./context-runtime/index.js";
import { DEFAULT_THINKING_LEVEL } from "./defaults.js";
import type { ExtensionRunner, LoadExtensionsResult, ToolDefinition } from "./extensions/index.js";
import { governancePolicyFromEnv } from "./governance/evaluator.js";
import { GovernanceService } from "./governance/service.js";
import { convertToLlm } from "./messages.js";
import { ModelRegistry } from "./model-registry.js";
import { findInitialModel } from "./model-resolver.js";
import type { ResourceLoader } from "./resource-loader.js";
import { DefaultResourceLoader } from "./resource-loader.js";
import { SessionManager } from "./session-manager.js";
import { SettingsManager } from "./settings-manager.js";
import { resolveSharedInferenceRuntime } from "./shared-inference/config.js";
import { time } from "./timings.js";
import {
	allTools,
	bashTool,
	codingTools,
	createBashTool,
	createCodingTools,
	createEditTool,
	createFindTool,
	createGrepTool,
	createLsTool,
	createPowerShellTool,
	createReadOnlyTools,
	createReadTool,
	createWriteTool,
	editTool,
	findTool,
	grepTool,
	lsTool,
	powershellTool,
	readOnlyTools,
	readTool,
	type Tool,
	type ToolName,
	writeTool,
} from "./tools/index.js";

/** Coerce a persisted virtualization record into its typed form, failing safe. */
function coerceToolVirtualizationRecord(value: unknown): ToolVirtualizationRecord | undefined {
	if (typeof value !== "object" || value === null) return undefined;
	const r = value as Record<string, unknown>;
	if (
		typeof r.toolCallId !== "string" ||
		typeof r.evidenceId !== "string" ||
		typeof r.contentHash !== "string" ||
		typeof r.source !== "string" ||
		typeof r.synopsis !== "string" ||
		typeof r.contentBytes !== "number" ||
		typeof r.virtualizedAtMs !== "number"
	) {
		return undefined;
	}
	return {
		toolCallId: r.toolCallId,
		evidenceId: r.evidenceId,
		contentHash: r.contentHash,
		source: r.source,
		synopsis: r.synopsis,
		contentBytes: r.contentBytes,
		virtualizedAtMs: r.virtualizedAtMs,
	};
}

export interface CreateAgentSessionOptions {
	/** Working directory for project-local discovery. Default: process.cwd() */
	cwd?: string;
	/** Global config directory. Default: ~/.pi/agent */
	agentDir?: string;

	/** Auth storage for credentials. Default: AuthStorage.create(agentDir/auth.json) */
	authStorage?: AuthStorage;
	/** Model registry. Default: new ModelRegistry(authStorage, agentDir/models.json) */
	modelRegistry?: ModelRegistry;

	/** Model to use. Default: from settings, else first available */
	model?: Model<any>;
	/** Fallback models to try if the primary model fails */
	fallbackModels?: Model<any>[];
	/** Thinking level. Default: from settings, else 'medium' (clamped to model capabilities) */
	thinkingLevel?: ThinkingLevel;
	/** Models available for cycling (Ctrl+P in interactive mode) */
	scopedModels?: Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }>;

	/** Built-in tools to use. Default: codingTools [read, bash, edit, write] */
	tools?: Tool[];
	/** Custom tools to register (in addition to built-in tools). */
	customTools?: ToolDefinition[];

	/** Resource loader. When omitted, DefaultResourceLoader is used. */
	resourceLoader?: ResourceLoader;

	/** Session manager. Default: SessionManager.create(cwd) */
	sessionManager?: SessionManager;

	/** Settings manager. Default: SettingsManager.create(cwd, agentDir) */
	settingsManager?: SettingsManager;

	/**
	 * Optional custom stream function. When omitted, the session uses the
	 * configured shared-inference scheduler stream (if active) or the default
	 * provider stream.
	 */
	streamFn?: StreamFn;
	/** Structured runtime correlation for durable mission governance. */
	missionId?: string;
	assignmentId?: string;
	executionId?: string;
}

/** Result from createAgentSession */
export interface CreateAgentSessionResult {
	/** The created session */
	session: AgentSession;
	/** Extensions result (for UI context setup in interactive mode) */
	extensionsResult: LoadExtensionsResult;
	/** Warning if session was restored with a different model than saved */
	modelFallbackMessage?: string;
}

// Re-exports

export type {
	ExtensionAPI,
	ExtensionCommandContext,
	ExtensionContext,
	ExtensionFactory,
	SlashCommandInfo,
	SlashCommandLocation,
	SlashCommandSource,
	ToolDefinition,
} from "./extensions/index.js";
export type { PromptTemplate } from "./prompt-templates.js";
export type { Skill } from "./skills.js";
export type { Tool } from "./tools/index.js";

export {
	allTools as allBuiltInTools,
	bashTool,
	codingTools,
	createBashTool,
	// Tool factories (for custom cwd)
	createCodingTools,
	createEditTool,
	createFindTool,
	createGrepTool,
	createLsTool,
	createReadOnlyTools,
	createReadTool,
	createWriteTool,
	createPowerShellTool,
	editTool,
	findTool,
	grepTool,
	lsTool,
	powershellTool,
	readOnlyTools,
	// Pre-built tools (use process.cwd())
	readTool,
	writeTool,
};

// Helper Functions

function getDefaultAgentDir(): string {
	return getAgentDir();
}

/**
 * Create an AgentSession with the specified options.
 *
 * @example
 * ```typescript
 * // Minimal - uses defaults
 * const { session } = await createAgentSession();
 *
 * // With explicit model
 * import { getModel } from '@apholdings/jensen-ai';
 * const { session } = await createAgentSession({
 *   model: getModel('anthropic', 'claude-opus-4-5'),
 *   thinkingLevel: 'high',
 * });
 *
 * // Continue previous session
 * const { session, modelFallbackMessage } = await createAgentSession({
 *   continueSession: true,
 * });
 *
 * // Full control
 * const loader = new DefaultResourceLoader({
 *   cwd: process.cwd(),
 *   agentDir: getAgentDir(),
 *   settingsManager: SettingsManager.create(),
 * });
 * await loader.reload();
 * const { session } = await createAgentSession({
 *   model: myModel,
 *   tools: [readTool, bashTool],
 *   resourceLoader: loader,
 *   sessionManager: SessionManager.inMemory(),
 * });
 * ```
 */
export async function createAgentSession(options: CreateAgentSessionOptions = {}): Promise<CreateAgentSessionResult> {
	const cwd = options.cwd ?? process.cwd();
	const agentDir = options.agentDir ?? getDefaultAgentDir();
	let resourceLoader = options.resourceLoader;

	// Use provided or create AuthStorage and ModelRegistry
	const authPath = options.agentDir ? join(agentDir, "auth.json") : undefined;
	const modelsPath = options.agentDir ? join(agentDir, "models.json") : undefined;
	const authStorage = options.authStorage ?? AuthStorage.create(authPath);
	const modelRegistry = options.modelRegistry ?? new ModelRegistry(authStorage, modelsPath);

	const settingsManager = options.settingsManager ?? SettingsManager.create(cwd, agentDir);
	const sessionManager = options.sessionManager ?? SessionManager.create(cwd);

	if (!resourceLoader) {
		resourceLoader = new DefaultResourceLoader({ cwd, agentDir, settingsManager });
		await resourceLoader.reload();
		time("resourceLoader.reload");
	}

	// Check if session has existing data to restore
	const existingSession = sessionManager.buildSessionContext();
	const hasExistingSession = existingSession.messages.length > 0;
	const hasThinkingEntry = sessionManager.getBranch().some((entry) => entry.type === "thinking_level_change");

	let model = options.model;
	let modelFallbackMessage: string | undefined;

	// If session has data, try to restore model from it
	if (!model && hasExistingSession && existingSession.model) {
		const restoredModel = modelRegistry.find(existingSession.model.provider, existingSession.model.modelId);
		if (restoredModel && (await modelRegistry.getApiKey(restoredModel))) {
			model = restoredModel;
		}
		if (!model) {
			modelFallbackMessage = `Could not restore model ${existingSession.model.provider}/${existingSession.model.modelId}`;
		}
	}

	// If still no model, use findInitialModel (checks settings default, then provider defaults)
	if (!model) {
		const result = await findInitialModel({
			scopedModels: [],
			isContinuing: hasExistingSession,
			defaultProvider: settingsManager.getDefaultProvider(),
			defaultModelId: settingsManager.getDefaultModel(),
			defaultThinkingLevel: settingsManager.getDefaultThinkingLevel(),
			modelRegistry,
		});
		model = result.model;
		if (!model) {
			modelFallbackMessage = `No models available. Use /login or set an API key environment variable. See ${join(getDocsPath(), "providers.md")}. Then use /model to select a model.`;
		} else if (modelFallbackMessage) {
			modelFallbackMessage += `. Using ${model.provider}/${model.id}`;
		}
	}

	let thinkingLevel = options.thinkingLevel;

	// If session has data, restore thinking level from it
	if (thinkingLevel === undefined && hasExistingSession) {
		thinkingLevel = hasThinkingEntry
			? (existingSession.thinkingLevel as ThinkingLevel)
			: (settingsManager.getDefaultThinkingLevel() ?? DEFAULT_THINKING_LEVEL);
	}

	// Fall back to settings default
	if (thinkingLevel === undefined) {
		thinkingLevel = settingsManager.getDefaultThinkingLevel() ?? DEFAULT_THINKING_LEVEL;
	}

	// Clamp to model capabilities
	if (!model || !model.reasoning) {
		thinkingLevel = "off";
	}

	// Determine initial active tools: explicit options.tools takes priority,
	// then settings config, then legacy defaults
	// Initial active tools: explicit options.tools takes priority, then settings
	// config, then legacy defaults. The legacy defaults expose the web research
	// family (web_search, web_fetch, deep_research, web_research_status) so that a
	// fresh session fulfills the 1.2.1 release contract: read-only web research is
	// available without requiring explicit --tools or settings configuration.
	const legacyDefaultTools: ToolName[] = [
		"read",
		"bash",
		"edit",
		"write",
		"todo_write",
		"todo_read",
		"todo_update",
		"memory_write",
		"retrieve_evidence",
		"web_search",
		"web_fetch",
		"deep_research",
		"web_research_status",
	];
	const configuredTools = settingsManager.getDefaultActiveToolNames();
	const initialActiveToolNames: ToolName[] = options.tools
		? options.tools.map((t) => t.name).filter((n): n is ToolName => n in allTools)
		: (configuredTools ?? legacyDefaultTools);

	let agent: Agent;

	// Create convertToLlm wrapper that filters images if blockImages is enabled (defense-in-depth)
	const convertToLlmWithBlockImages = (messages: AgentMessage[]): Message[] => {
		const converted = convertToLlm(messages);
		// Check setting dynamically so mid-session changes take effect
		if (!settingsManager.getBlockImages()) {
			return converted;
		}
		// Filter out ImageContent from all messages, replacing with text placeholder
		return converted.map((msg) => {
			if (msg.role === "user" || msg.role === "toolResult") {
				const content = msg.content;
				if (Array.isArray(content)) {
					const hasImages = content.some((c) => c.type === "image");
					if (hasImages) {
						const filteredContent = content
							.map((c) =>
								c.type === "image" ? { type: "text" as const, text: "Image reading is disabled." } : c,
							)
							.filter(
								(c, i, arr) =>
									// Dedupe consecutive "Image reading is disabled." texts
									!(
										c.type === "text" &&
										c.text === "Image reading is disabled." &&
										i > 0 &&
										arr[i - 1].type === "text" &&
										(arr[i - 1] as { type: "text"; text: string }).text === "Image reading is disabled."
									),
							);
						return { ...msg, content: filteredContent };
					}
				}
			}
			return msg;
		});
	};

	const extensionRunnerRef: { current?: ExtensionRunner } = {};

	// Cold evidence archive (L3) is created once and shared by the governor (for
	// virtualization) and the model-facing retrieve_evidence capability (for
	// bounded rehydration). A single store means evidence references remain
	// resolvable across rollovers and session resumes.
	const evidenceArchive = new EvidenceFileStore(join(agentDir, "context-evidence"));

	// Long-Horizon Context Virtualization (2.5.0). The governor is the preflight
	// boundary that keeps every request within the safe input budget. It is
	// wired into transformContext below and its checkpoint provider is bound to
	// the AgentSession after construction.
	const governorSettings = settingsManager.getContextGovernorSettings();
	const contextCapabilityOverrides: ContextCapabilityOverrides = {
		configuredContextWindow: governorSettings.configuredContextWindow,
		reservedOutputTokens: governorSettings.reservedOutputTokens,
		safetyReserveTokens: governorSettings.safetyReserveTokens,
		softPressureRatio: governorSettings.softPressureRatio,
	};
	const contextGovernor = governorSettings.enabled
		? new ContextGovernor({
				capability: resolveContextCapability(
					{
						modelContextWindow: model?.contextWindow ?? 0,
						modelMaxTokens: model?.maxTokens ?? 8192,
						reasoning: model?.reasoning,
					},
					contextCapabilityOverrides,
				),
				archive: evidenceArchive,
				capabilityProvider: () => {
					const currentModel = agent.state.model;
					if (!currentModel?.contextWindow) return undefined as never;
					return resolveContextCapability(
						{
							modelContextWindow: currentModel.contextWindow,
							modelMaxTokens: currentModel.maxTokens ?? 8192,
							reasoning: currentModel.reasoning,
						},
						contextCapabilityOverrides,
					);
				},
				keepRecentTokens: governorSettings.keepRecentTokens,
				toolResultVirtualizeThreshold: governorSettings.toolResultVirtualizeThreshold,
				// Durable virtualization provenance: tool results that were already
				// archived stay virtualized across requests, session resume, process
				// restart, and durable child resume.
				initialVirtualizations: sessionManager
					.getLatestSessionToolVirtualizations()
					.map(coerceToolVirtualizationRecord)
					.filter((r): r is NonNullable<typeof r> => r !== undefined),
				initialEvidenceRefs: sessionManager.getLatestSessionEvidenceRefs(),
				virtualizationProvider: () =>
					sessionManager
						.getLatestSessionToolVirtualizations()
						.map(coerceToolVirtualizationRecord)
						.filter((r): r is NonNullable<typeof r> => r !== undefined),
				virtualizationSink: (records) => {
					sessionManager.appendSessionToolVirtualizations(records);
				},
			})
		: undefined;

	const governance = options.missionId ? new GovernanceService({ policy: governancePolicyFromEnv() }) : undefined;
	const sharedInference = resolveSharedInferenceRuntime({
		sessionId: sessionManager.getSessionId(),
		missionId: options.missionId ?? process.env.JENSEN_MISSION_ID,
		assignmentId: options.assignmentId ?? process.env.JENSEN_ASSIGNMENT_ID,
		executionId: options.executionId ?? process.env.JENSEN_EXECUTION_ID,
		governance,
	});

	agent = new Agent({
		initialState: {
			systemPrompt: "",
			model,
			thinkingLevel,
			tools: [],
		},
		streamFn: options.streamFn ?? sharedInference?.streamFn,
		convertToLlm: convertToLlmWithBlockImages,
		onPayload: async (payload, model) => {
			const runner = extensionRunnerRef.current;
			let finalPayload = payload;
			if (runner?.hasHandlers("before_provider_request")) {
				finalPayload = await runner.emitBeforeProviderRequest(payload);
			}
			if (model.api === "openai-completions") {
				assertValidChatCompletionsPayload(finalPayload, model.provider, model.id);
			} else if (model.api.endsWith("-responses")) {
				assertValidResponsesPayload(finalPayload, model.provider, model.id);
			}
			return finalPayload;
		},
		sessionId: sessionManager.getSessionId(),
		transformContext: async (messages) => {
			const runner = extensionRunnerRef.current;
			const withExtensions = runner ? await runner.emitContext(messages) : messages;
			if (!contextGovernor) return withExtensions;
			const currentModel = agent.state.model;
			if (!currentModel?.contextWindow) return withExtensions;
			const result = await contextGovernor.govern({
				systemPrompt: agent.state.systemPrompt,
				dynamicPrompt: agent.state.dynamicPrompt,
				messages: withExtensions,
				tools: agent.state.tools,
			});
			if (process.env.JENSEN_CONTEXT_TELEMETRY) {
				process.stderr.write(`[context-governor] ${JSON.stringify(result.diagnostics)}\n`);
			}
			return result.assembly.messages;
		},
		steeringMode: settingsManager.getSteeringMode(),
		followUpMode: settingsManager.getFollowUpMode(),
		transport: settingsManager.getTransport(),
		thinkingBudgets: settingsManager.getThinkingBudgets(),
		maxRetryDelayMs: settingsManager.getRetrySettings().maxDelayMs,
		getApiKey: async (provider) => {
			// Use the provider argument from the in-flight request;
			// agent.state.model may already be switched mid-turn.
			const resolvedProvider = provider || agent.state.model?.provider;
			if (!resolvedProvider) {
				throw new Error("No model selected");
			}
			const key = await modelRegistry.getApiKeyForProvider(resolvedProvider);
			if (!key) {
				const model = agent.state.model;
				const isOAuth = model && modelRegistry.isUsingOAuth(model);
				if (isOAuth) {
					throw new Error(
						`Authentication failed for "${resolvedProvider}". ` +
							`Credentials may have expired or network is unavailable. ` +
							`Run '/login ${resolvedProvider}' to re-authenticate.`,
					);
				}
				throw new Error(
					`No API key found for "${resolvedProvider}". ` +
						`Set an API key environment variable or run '/login ${resolvedProvider}'.`,
				);
			}
			return key;
		},
	});

	// Restore messages if session has existing data
	if (hasExistingSession) {
		agent.replaceMessages(existingSession.messages);
		if (!hasThinkingEntry) {
			sessionManager.appendThinkingLevelChange(thinkingLevel);
		}
	} else {
		// Save initial model and thinking level for new sessions so they can be restored on resume
		if (model) {
			sessionManager.appendModelChange(model.provider, model.id);
		}
		sessionManager.appendThinkingLevelChange(thinkingLevel);
	}

	const session = new AgentSession({
		agent,
		sessionManager,
		settingsManager,
		cwd,
		scopedModels: options.scopedModels,
		fallbackModels: options.fallbackModels,
		resourceLoader,
		customTools: options.customTools,
		modelRegistry,
		initialActiveToolNames,
		extensionRunnerRef,
		evidenceArchive,
		contextGovernor,
	});
	const extensionsResult = resourceLoader.getExtensions();
	if (governance && options.missionId) {
		const seenToolCalls = new Set<string>();
		let turnSequence = 0;
		let retrySequence = 0;
		session.subscribe((event) => {
			void (async () => {
				if (event.type === "turn_end") {
					turnSequence += 1;
					await governance.consume(options.missionId!, {
						eventId: `${sessionManager.getSessionId()}:turn:${turnSequence}`,
						scope: "session",
						resource: "turns",
						amount: 1,
						atMs: Date.now(),
					});
				} else if (event.type === "tool_execution_start" && !seenToolCalls.has(event.toolCallId)) {
					seenToolCalls.add(event.toolCallId);
					await governance.consume(options.missionId!, {
						eventId: `${sessionManager.getSessionId()}:tool:${event.toolCallId}`,
						scope: "session",
						resource: "toolCalls",
						amount: 1,
						atMs: Date.now(),
					});
				} else if (event.type === "auto_retry_start") {
					retrySequence += 1;
					await governance.recordRetry(
						options.missionId!,
						`${sessionManager.getSessionId()}:provider-retry:${retrySequence}`,
						"provider",
						Date.now(),
					);
				}
			})().catch(() => {
				// Governance failures are surfaced by the governed stream/admission path;
				// event observers never create an unhandled session rejection.
			});
		});
	}

	// Bind the governor's checkpoint provider to the session's durable-projection
	// checkpoint so a rollover rehydrates operational state across the SAME mission.
	// Evidence references are persisted so the model can page old cold artifacts
	// back after rollover via retrieve_evidence.
	contextGovernor?.setCheckpointProvider(() =>
		session.buildMissionContextCheckpoint(contextGovernor.getArchivedEvidenceRefs()),
	);

	// Persist newly-archived evidence references into the session so a durable
	// child can retrieve pre-interruption evidence after explicit resume.
	contextGovernor?.setEvidenceRefsSink((refs) => {
		sessionManager.appendSessionEvidenceRefs(refs);
	});

	return {
		session,
		extensionsResult,
		modelFallbackMessage,
	};
}
